import React, { useCallback, useEffect, useState } from 'react'

import { useDocumentCardContext } from '@frame/ui-components/document-card/context'
import { P } from '@frame/ui-primitives/p'

import { DataAttribute } from '@frame/types'

const CONFIG = {
  loading_no_classification: {
    maxPercent: 98,
    maxPercentSlowDownThreshold: 95,
    intervalSpeed: 750,
    increaseFactor: 35,
  },
  loading: { maxPercent: 98, maxPercentSlowDownThreshold: 90, intervalSpeed: 500, increaseFactor: 15 },
  complete: { maxPercent: 100, maxPercentSlowDownThreshold: 100, intervalSpeed: 250, increaseFactor: 35 },
}

type Props = {
  isLoading: boolean
}

const DISPLAY_MESSAGES_FROM_PERCENT: Record<number, string> = {
  0: 'Uploading file...',
  20: 'Classifying content...',
  40: 'Analyzing document...',
  80: 'Almost there...',
}

const DISPLAY_MESSAGES_FROM_PERCENT_NO_CLASSIFICATION: Record<number, string> = {
  0: 'Uploading file...',
  80: 'Almost there...',
}

export function LoadingBar({ isLoading }: Props) {
  const { hideClassification } = useDocumentCardContext()

  const [percentage, setPercentage] = useState(0)
  const [displayMessage, setDisplayMessage] = useState(DISPLAY_MESSAGES_FROM_PERCENT[0])

  const { maxPercent, intervalSpeed, increaseFactor, maxPercentSlowDownThreshold } = isLoading
    ? hideClassification
      ? CONFIG.loading_no_classification
      : CONFIG.loading
    : CONFIG.complete

  const updateDisplayMessage = useCallback(() => {
    // Get an array of the threshold numbers from the object keys.
    const messages = hideClassification
      ? DISPLAY_MESSAGES_FROM_PERCENT_NO_CLASSIFICATION
      : DISPLAY_MESSAGES_FROM_PERCENT
    const thresholds = Object.keys(messages).map(Number)

    // Find the largest threshold that is not greater than the current percentage.
    const applicableThreshold = Math.max(...thresholds.filter((threshold) => percentage >= threshold))

    // Return the message corresponding to that threshold.
    setDisplayMessage(messages[applicableThreshold])
  }, [percentage, hideClassification, setDisplayMessage])

  useEffect(() => {
    const interval = setInterval(() => {
      if (percentage >= maxPercent) return

      setPercentage((prev) => {
        // Once we hit maxPercent, increase by 1 until we reach 99%
        if (prev >= maxPercentSlowDownThreshold) {
          return Math.min(prev + 1, maxPercent)
        }

        const increase = Math.floor(Math.random() * increaseFactor) + 1
        return Math.min(prev + increase, maxPercentSlowDownThreshold)
      })
      updateDisplayMessage()
    }, intervalSpeed)

    return () => clearInterval(interval)
  }, [percentage, maxPercent, maxPercentSlowDownThreshold, increaseFactor, intervalSpeed, updateDisplayMessage])

  return (
    <>
      <div data-cy={DataAttribute.LOADING_BAR} className="relative h-1 w-full">
        <div className="absolute h-full w-full bg-moss-300" />
        <div style={{ width: `${percentage}%` }} className="absolute h-full bg-cobranding transition-all" />
      </div>
      <P className="text-sm text-moss-500">{displayMessage}</P>
    </>
  )
}
